import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { API_URL } from './constants';
import type {
  DashboardStats,
  ProfitLoss,
  ExpenseCategory,
  BankAccount,
  CashFlowPoint,
  Transaction,
} from './types';

export type DashboardPayload = {
  stats: DashboardStats;
  profitLoss: ProfitLoss;
  expenseCategories: ReadonlyArray<ExpenseCategory>;
  bankAccounts: ReadonlyArray<BankAccount>;
  cashFlow: ReadonlyArray<CashFlowPoint>;
  recentTransactions: ReadonlyArray<Transaction>;
};

// Defensive empty payload used while loading or on error
export const EMPTY_PAYLOAD: DashboardPayload = {
  stats: {
    revenueThisMonth: 0,
    expensesThisMonth: 0,
    cashOnHand: null,
    nextPayrollDate: null,
    nextPayrollStatus: null,
    revenueDeltaPct: 0,
  },
  profitLoss: {
    periodLabel: 'Last month',
    netProfit: 0,
    income: 0,
    expenses: 0,
    deltaPct: 0,
  },
  expenseCategories: [],
  bankAccounts: [],
  cashFlow: [],
  recentTransactions: [],
};

async function fetchDashboard(): Promise<DashboardPayload> {
  const token = localStorage.getItem('token') || '';
  const headers = { Authorization: 'Bearer ' + token };
  try {
    const res = await axios.get<DashboardPayload>(API_URL + '/dashboard/', { headers });
    return { ...EMPTY_PAYLOAD, ...(res.data || {}) };
  } catch {
    return EMPTY_PAYLOAD;
  }
}

export function useDashboardData() {
  return useQuery({
    queryKey: ['dashboard'],
    queryFn: fetchDashboard,
    staleTime: 60_000,
    retry: 1,
    placeholderData: EMPTY_PAYLOAD,
  });
}
